/**
 * Cross-tab sign-out broadcast.
 *
 * Quando o usuário clica em sign-out em uma aba, todas as outras abas devem
 * sair também — sem fazer nova chamada à API. Solução: BroadcastChannel API
 * (suportada em todos os browsers modernos; ausente em Capacitor WebView
 * antigo, daí o fallback no-op).
 *
 * Mensagens trafegam como objetos { type: "SIGN_OUT", origin: <random> }.
 * O `origin` permite ignorar mensagens enviadas por nós mesmos.
 */

export interface BroadcastSignOutChannel {
  /** Envia evento SIGN_OUT para outras abas. */
  publish(): void;
  /** Escuta SIGN_OUT vindo de outras abas. Devolve cleanup. */
  subscribe(handler: () => void): () => void;
  /** Fecha o channel. Idempotente. */
  close(): void;
}

interface BroadcastMessage {
  type: "SIGN_OUT";
  origin: string;
}

const NOOP_CHANNEL: BroadcastSignOutChannel = {
  publish: () => {},
  subscribe: () => () => {},
  close: () => {},
};

/**
 * Cria channel para o nome dado. Retorna no-op em ambientes sem
 * BroadcastChannel (ex: SSR, jsdom sem polyfill, WebView antigo).
 */
export function createBroadcastSignOutChannel(
  channelName: string
): BroadcastSignOutChannel {
  if (typeof BroadcastChannel === "undefined") {
    return NOOP_CHANNEL;
  }

  const origin = randomOrigin();
  const channel = new BroadcastChannel(channelName);
  let closed = false;

  return {
    publish() {
      if (closed) return;
      const msg: BroadcastMessage = { type: "SIGN_OUT", origin };
      channel.postMessage(msg);
    },
    subscribe(handler) {
      if (closed) return () => {};
      const listener = (event: MessageEvent<unknown>) => {
        if (!isBroadcastMessage(event.data)) return;
        if (event.data.origin === origin) return; // ignora self
        if (event.data.type === "SIGN_OUT") handler();
      };
      channel.addEventListener("message", listener);
      return () => channel.removeEventListener("message", listener);
    },
    close() {
      if (closed) return;
      closed = true;
      channel.close();
    },
  };
}

function isBroadcastMessage(data: unknown): data is BroadcastMessage {
  if (typeof data !== "object" || data === null) return false;
  const maybe = data as { type?: unknown; origin?: unknown };
  return (
    typeof maybe.type === "string" &&
    typeof maybe.origin === "string" &&
    maybe.type === "SIGN_OUT"
  );
}

function randomOrigin(): string {
  const cryptoObj: Crypto | undefined =
    typeof globalThis !== "undefined" && "crypto" in globalThis
      ? (globalThis.crypto as Crypto)
      : undefined;
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint32Array(2);
    cryptoObj.getRandomValues(buf);
    return `${buf[0].toString(36)}-${buf[1].toString(36)}`;
  }
  return `${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}
